import { type BlockConfig, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `heroPoster` block.
 *
 * Field tree mirrors the CMS-authorable structure of `./hero-poster.tsx`:
 * a 2-column poster-typography hero with an image on one side, a HUGE
 * uppercase wordmark + subhead + app-store buttons on the other.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `hero-poster.tsx` / `hero-poster.payload.ts`
 *   React component export:  `HeroPoster`
 *   Payload `slug`:          `heroPoster`
 *   Payload `interfaceName`: `HeroPosterBlock`
 */

export const HeroPosterBlock: BlockConfig = {
  slug: 'heroPoster',
  interfaceName: 'HeroPosterBlock',
  labels: {
    singular: 'Poster Hero',
    plural: 'Poster Hero Blocks',
  },
  fields: [
    {
      name: 'image',
      type: 'group',
      required: true,
      label: 'Image',
      fields: [
        { name: 'src', type: 'text', required: true, label: 'Image URL' },
        { name: 'alt', type: 'text', required: true, label: 'Alt text' },
      ],
    },
    {
      name: 'imagePosition',
      type: 'select',
      defaultValue: 'left',
      label: 'Image position',
      options: [
        { label: 'Left (image LEFT, headline RIGHT)', value: 'left' },
        { label: 'Right (headline LEFT, image RIGHT)', value: 'right' },
      ],
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: {
        rows: 2,
        description:
          'Uppercase poster-scale headline. Press Enter to force a line break -- each visual line of the headline renders on its own row (e.g. "Download" Enter "the App" -> two visual lines). The component splits on actual newline characters at render time. The TypeScript data file in this repo uses the `\\n` escape sequence inside a string literal ("Download\\nthe App") which evaluates to the same newline character.',
      },
    },
    {
      name: 'subheadline',
      type: 'text',
      label: 'Subheadline',
    },
    {
      name: 'storeButtons',
      type: 'array',
      label: 'App-store buttons',
      labels: { singular: 'Button', plural: 'Buttons' },
      maxRows: 3,
      fields: [
        {
          name: 'provider',
          type: 'select',
          required: true,
          label: 'Store provider',
          options: [
            { label: 'Apple App Store', value: 'appStore' },
            { label: 'Google Play', value: 'googlePlay' },
          ],
        },
        linkField({ name: 'url', label: 'Store URL', required: true }),
        {
          name: 'eyebrowText',
          type: 'text',
          label: 'Eyebrow text (override)',
          admin: {
            description:
              'Defaults: "Download on the" (App Store) / "Get it on" (Google Play).',
          },
        },
        {
          name: 'labelText',
          type: 'text',
          label: 'Label text (override)',
          admin: {
            description: 'Defaults: "App Store" / "Google Play".',
          },
        },
      ],
    },
  ],
};

export default HeroPosterBlock;
